// Investment analysis - admin only, stored in Firestore
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  writeBatch,
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore'
import { db } from './config'

const INVESTMENT_ANALYSES_REF = (db) => collection(db, 'admin', 'investment', 'analyses')

/**
 * Save investment analysis data to Firestore
 * Splits array into individual documents to avoid 1MB document limit
 * @param {Array} analyses - Array of investor analysis objects
 * @returns {Promise<{count: number}>}
 */
export const saveInvestmentAnalyses = async (analyses) => {
  if (!Array.isArray(analyses) || analyses.length === 0) {
    throw new Error('Invalid or empty analysis data')
  }

  const investmentRef = doc(db, 'admin', 'investment')

  // Clear existing analyses by deleting metadata (we'll overwrite all)
  // First get existing doc IDs to delete - actually we'll use setDoc with merge
  // Better approach: delete all docs in collection then add new ones
  // Firestore batch has limit of 500 ops - we may need multiple batches

  const existingSnap = await getDocs(INVESTMENT_ANALYSES_REF(db))
  const deleteBatch = writeBatch(db)
  let deleteCount = 0
  existingSnap.docs.forEach((d) => {
    deleteBatch.delete(d.ref)
    deleteCount++
    if (deleteCount >= 450) {
      // Stay under 500 limit
      return
    }
  })
  if (deleteCount > 0) {
    await deleteBatch.commit()
  }

  // If we had more than 450 docs, delete rest in another batch
  if (existingSnap.docs.length > 450) {
    const remaining = existingSnap.docs.slice(450)
    const secondDeleteBatch = writeBatch(db)
    remaining.forEach((d) => secondDeleteBatch.delete(d.ref))
    await secondDeleteBatch.commit()
  }

  // Add new analyses - batch limit 500, so we'll do in chunks
  const BATCH_SIZE = 450
  for (let i = 0; i < analyses.length; i += BATCH_SIZE) {
    const chunk = analyses.slice(i, i + BATCH_SIZE)
    const addBatch = writeBatch(db)
    chunk.forEach((analysis, idx) => {
      const docId = `analysis_${i + idx}`
      const ref = doc(db, 'admin', 'investment', 'analyses', docId)
      addBatch.set(ref, {
        ...analysis,
        index: i + idx,
        uploadedAt: serverTimestamp()
      })
    })
    await addBatch.commit()
  }

  // Update metadata (stored in the investment document)
  await setDoc(investmentRef, {
    count: analyses.length,
    uploadedAt: serverTimestamp(),
    lastUpdated: serverTimestamp()
  }, { merge: true })

  return { count: analyses.length }
}

/**
 * Get all investment analyses from Firestore
 * @returns {Promise<Array>} Array of investor analysis objects
 */
export const getInvestmentAnalyses = async () => {
  const analysesRef = INVESTMENT_ANALYSES_REF(db)
  const q = query(analysesRef, orderBy('index'))
  const snapshot = await getDocs(q)

  return snapshot.docs
    .map((d) => d.data())
    .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
    .map(({ index, uploadedAt, ...analysis }) => analysis)
}

/**
 * Get investment metadata (count, last upload time)
 * @returns {Promise<{count: number, uploadedAt: any} | null>}
 */
export const getInvestmentMetadata = async () => {
  const investmentRef = doc(db, 'admin', 'investment')
  const snap = await getDoc(investmentRef)
  if (snap.exists()) {
    return snap.data()
  }
  return null
}
